import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@workos-inc/authkit-react";
import { POSITIONS } from "../../../shared/constants/userConstants";
import { assignableStationRoles } from "../../../shared/constants/roleConstants";
import { NewUser, newUserSchema } from "../../../shared/utils/userValidation";
import { profileClient } from "@/shared/api/clients";
import { extractApiError } from "@/shared/api/apiError";
import { useOrgId } from "../../../shared/auth/orgContext";

/**
 * Hard-state 409/403s: the admin must change something before retrying, so
 * they get a persistent inline alert instead of a 6-second toast.
 */
function blockingErrorMessage(
  code: string | null,
  message: string | null,
  details: Record<string, unknown> | null,
): string | null {
  if (code === "seat_limit_reached") {
    const limit = details?.seat_limit;
    const used = details?.seats_used;
    const usage =
      typeof limit === "number" && typeof used === "number"
        ? `Your station is using ${used} of its ${limit} seats`
        : "All of your station's seats are in use";
    return `${usage} (invitations that haven't been accepted yet hold a seat). Deactivate a user to free a seat, or contact KARDA to increase your station's limit.`;
  }
  if (code === "station_inactive") {
    return "This station is inactive, so users can't be added. Contact KARDA support to reactivate it.";
  }
  if (code === "role_escalation_denied") {
    return 'Your account isn\'t allowed to grant administrator privileges. Uncheck "Grant Administrator Privileges" and try again.';
  }
  if (code === "already_member") {
    return message ?? "This person already has an account in your station.";
  }
  return null;
}

export interface AddUserFormProps {
  onUserAdded: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  stationId?: string;
}

const initialUser: NewUser = {
  firstName: "",
  lastName: "",
  position: "",
  badgeNumber: "",
  department: "",
  email: "",
  isAdmin: false,
  bodycamVendorId: "",
};

interface BodycamMappingResult {
  status: "created" | "deferred" | "failed";
  externalUserId: string;
  message?: string;
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
  },
};

const AddUserForm: React.FC<AddUserFormProps> = ({
  onUserAdded,
  onError,
  onSuccess,
  stationId,
}) => {
  const theme = useTheme();
  const organizationId = useOrgId();
  const effectiveStationId = stationId ?? organizationId;
  const { role } = useAuth();
  // Mirrors the backend's assign rule (at or below the caller's own rank):
  // station admins and KARDA admins can both grant station_admin.
  const canGrantAdmin = assignableStationRoles(role ?? null).some(
    (assignable) => assignable.slug === "station_admin",
  );

  const [blockingError, setBlockingError] = useState<string | null>(null);
  // The user WAS added but their bodycam vendor ID wasn't linked — a
  // persistent warning, not a toast, because the admin has follow-up to do.
  const [mappingWarning, setMappingWarning] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<NewUser>({
    resolver: zodResolver(newUserSchema),
    mode: "onBlur",
    defaultValues: initialUser,
  });

  // zodResolver hands the parsed (trimmed) values to the submit handler.
  const onSubmit = handleSubmit(async (values) => {
    if (!effectiveStationId) {
      onError(
        "Unable to add user: station or organization ID is missing. Please refresh and try again.",
      );
      return;
    }

    setBlockingError(null);
    setMappingWarning(null);
    try {
      const { data } = await profileClient.post<{
        action?: string;
        bodycamMapping?: BodycamMappingResult;
      }>(`/v1/stations/${effectiveStationId}/users`, {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        isAdmin: values.isAdmin ?? false,
        metadata: {
          division_name: values.department,
          job_title: values.position,
          badge_number: values.badgeNumber,
        },
        ...(values.bodycamVendorId
          ? { bodycamExternalUserId: values.bodycamVendorId }
          : {}),
        sendOnboardingEmail: true,
      });

      // The backend distinguishes what actually happened — say so instead of
      // claiming a new account for what was really a resent invite.
      const baseMessage =
        data?.action === "invitation_resent"
          ? `${values.email} already had a pending invitation — it was re-sent. They still need to accept it.`
          : data?.action === "user_updated_membership_reactivated"
            ? `${values.email} already had an account here — it was re-activated and updated.`
            : `Invitation sent to ${values.email}.`;
      const mappingNote =
        data?.bodycamMapping?.status === "created"
          ? " Their bodycam vendor ID was linked."
          : data?.bodycamMapping?.status === "deferred"
            ? " Their bodycam vendor ID will be linked when they accept the invitation."
            : "";
      if (data?.bodycamMapping?.status === "failed") {
        // Composed here rather than echoing the backend's message (which is
        // written for API consumers), and includes the vendor ID because the
        // form reset below clears the field the admin typed it into.
        setMappingWarning(
          `${values.email} was added, but bodycam vendor ID "${data.bodycamMapping.externalUserId}" could not be linked. Keep the ID handy and retry later, or contact KARDA support.`,
        );
      }
      onSuccess(baseMessage + mappingNote);
      reset(initialUser);
      onUserAdded();
    } catch (error) {
      const { message, code, details } = extractApiError(error);
      const blocking = blockingErrorMessage(code, message, details);
      if (blocking) {
        setBlockingError(blocking);
      } else {
        onError(message || "Failed to add user. Please try again.");
      }
    }
  });

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[400]}`,
        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
          }}
        >
          <PersonAddIcon />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Add Individual User
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Create a new user account manually
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ flex: 1 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="First Name"
            required
            error={!!errors.firstName}
            helperText={errors.firstName?.message ?? ""}
            sx={fieldSx}
            {...register("firstName")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Last Name"
            required
            error={!!errors.lastName}
            helperText={errors.lastName?.message ?? ""}
            sx={fieldSx}
            {...register("lastName")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="position"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth required error={!!errors.position}>
                <InputLabel id="rank-label">Rank</InputLabel>
                <Select
                  labelId="rank-label"
                  label="Rank"
                  sx={{ borderRadius: 2 }}
                  {...field}
                >
                  {POSITIONS.map((rank) => (
                    <MenuItem key={rank} value={rank}>
                      {rank}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.position?.message ?? ""}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Badge Number"
            required
            error={!!errors.badgeNumber}
            helperText={errors.badgeNumber?.message ?? ""}
            sx={fieldSx}
            {...register("badgeNumber")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Department"
            required
            error={!!errors.department}
            helperText={errors.department?.message ?? ""}
            sx={fieldSx}
            {...register("department")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Bodycam Vendor ID"
            error={!!errors.bodycamVendorId}
            helperText={
              errors.bodycamVendorId?.message ??
              "(Optional) this officer's user ID in your bodycam vendor's system"
            }
            sx={fieldSx}
            {...register("bodycamVendorId")}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            required
            error={!!errors.email}
            helperText={errors.email?.message ?? ""}
            sx={fieldSx}
            {...register("email")}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          {canGrantAdmin ? (
            <Controller
              name="isAdmin"
              control={control}
              render={({ field }) => (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Grant Administrator Privileges"
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", ml: 4 }}
                  >
                    Admins manage station users, roles, and settings, and can
                    view and manage every station report, incident, and piece of
                    evidence.
                  </Typography>
                </Box>
              )}
            />
          ) : (
            // The backend rejects grants above the caller's own rank, so
            // don't offer a control that can only fail.
            <Tooltip title="Your role can't grant admin privileges. Ask a station or KARDA administrator if this user needs admin access.">
              <FormControlLabel
                control={<Checkbox checked={false} disabled />}
                label="Grant Administrator Privileges (admins only)"
              />
            </Tooltip>
          )}
        </Grid>
        {blockingError && (
          <Grid size={{ xs: 12 }}>
            <Alert
              severity="error"
              onClose={() => setBlockingError(null)}
              sx={{ borderRadius: 2 }}
            >
              {blockingError}
            </Alert>
          </Grid>
        )}
        {mappingWarning && (
          <Grid size={{ xs: 12 }}>
            <Alert
              severity="warning"
              onClose={() => setMappingWarning(null)}
              sx={{ borderRadius: 2 }}
            >
              {mappingWarning}
            </Alert>
          </Grid>
        )}
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: "auto",
            pt: 1,
          }}
        >
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={isSubmitting || !effectiveStationId}
            startIcon={<PersonAddIcon />}
            sx={{
              width: 200,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              py: 1.2,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              "&:hover": {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            }}
          >
            {isSubmitting ? "Adding User..." : "Add User"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddUserForm;
